import { useQuery } from '@apollo/client'
import { useTheme } from '@emotion/react'
import { Box, Divider, Stack, Typography } from '@mui/material'
import Mention from '@tiptap/extension-mention'
import StarterKit from '@tiptap/starter-kit'
import { gql } from '__generated__'
import { GetRosterBlastsQuery } from '__generated__/graphql'
import { RichTextReadOnly } from 'mui-tiptap'
import { FC, useCallback, useEffect, useRef } from 'react'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import UpdatedText from 'shared/components/UpdatedText/UpdatedText'
import { getFullName } from 'shared/utils'
import { GetMergeFields } from '../BulkActions/BulkActions.gql'

interface HistoricalBlastsProps {
    rosterId: string
}

const LIMIT = 10

const GetRosterBlasts = gql(/* GraphQL */ `
    query GetRosterBlasts(
        $rosterId: ID!
        $page: Int
        $limit: Int
        $searches: SearchObject
        $filters: [FilterObject!]
        $sort: OrderObject
    ) {
        rosterBlasts(
            rosterId: $rosterId
            page: $page
            limit: $limit
            searches: $searches
            filters: $filters
            sort: $sort
        ) {
            data {
                id
                blast {
                    subject
                    content
                    updatedAt
                    updatedByUser {
                        firstName
                        nickname
                        lastName
                    }
                    createdAt
                    createdByUser {
                        firstName
                        nickname
                        lastName
                    }
                }
            }
            pageInfo {
                itemTotal
                pageNumber
                pageTotal
            }
        }
    }
`)

const HistoricalBlasts: FC<HistoricalBlastsProps> = ({ rosterId }) => {
    const theme = useTheme()
    const observer = useRef<IntersectionObserver>(
        new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                loadMoreData()
            }
        })
    )
    const observedElementRef = useRef<HTMLDivElement | null>(null)
    const nextPageToFetch = useRef<number>(2)

    const { data, fetchMore, loading } = useQuery(GetRosterBlasts, {
        variables: { page: 1, limit: LIMIT, rosterId },
        fetchPolicy: 'network-only',
    })

    const { data: mergeFields } = useQuery(GetMergeFields)

    const convertDelimetersToTipTapMention = useCallback(
        (message: string | null | undefined) => {
            if (!message) return ''
            const openDelimeter = mergeFields?.mergeFieldDelimiters.open || '{{'
            const closeDelimeter =
                mergeFields?.mergeFieldDelimiters.close || '}}'
            let messageToReturn = message

            for (let field of mergeFields?.mergeFields || []) {
                messageToReturn = messageToReturn.replaceAll(
                    `${openDelimeter}${field.mergeKey}${closeDelimeter}`,
                    `<span class="mention" data-type="mention" data-id="${field.mergeKey}">@${field.mergeKey}</span>`
                )
            }

            return messageToReturn
        },
        [mergeFields]
    )

    const loadMoreData = () => {
        fetchMore({
            variables: {
                page: nextPageToFetch.current,
                limit: LIMIT,
            },
            updateQuery(
                previousData,
                { fetchMoreResult, variables: { page, limit } }
            ) {
                if (
                    !previousData?.rosterBlasts?.data ||
                    !fetchMoreResult?.rosterBlasts?.data
                )
                    return fetchMoreResult ?? previousData
                const updatedRosterBlastsData = Array.from(
                    previousData.rosterBlasts.data
                )
                const pageLimit = previousData.rosterBlasts.pageInfo.pageTotal
                nextPageToFetch.current =
                    page + 1 <= pageLimit ? page + 1 : page
                for (
                    let i = 0;
                    i < fetchMoreResult.rosterBlasts.data.length;
                    i++
                ) {
                    updatedRosterBlastsData[(page - 1) * limit + i] =
                        fetchMoreResult.rosterBlasts.data[i]
                }
                const newCache = {
                    ...previousData,
                    rosterBlasts: {
                        ...fetchMoreResult.rosterBlasts,
                        data: updatedRosterBlastsData,
                    },
                }
                if (
                    fetchMoreResult?.rosterBlasts?.pageInfo.itemTotal ===
                    updatedRosterBlastsData.length
                ) {
                    observer.current?.disconnect()
                }
                return newCache
            },
        })
    }

    useEffect(() => {
        const _observer = observer.current
        // update which element we are observing as data changes
        if (observedElementRef.current) {
            _observer?.observe(observedElementRef.current)
        }

        return () => {
            if (_observer) _observer.disconnect()
        }
    }, [data])

    const cards = data?.rosterBlasts.data.map(
        (
            { blast }: GetRosterBlastsQuery['rosterBlasts']['data'][number],
            index
        ) => {
            const isLastItem = index === data?.rosterBlasts?.data.length - 1
            const message = convertDelimetersToTipTapMention(blast?.content)
            return (
                <Stack gap={2} ref={isLastItem ? observedElementRef : null}>
                    <Typography variant="h5">{blast?.subject}</Typography>
                    <UpdatedText
                        updatedAt={
                            blast?.updatedAt || blast?.createdAt || undefined
                        }
                        updatedBy={getFullName(
                            blast?.updatedByUser || blast?.createdByUser || {}
                        )}
                    />
                    <Box sx={{ color: theme.palette.other.secondary_light }}>
                        <RichTextReadOnly
                            extensions={[
                                StarterKit,
                                Mention.configure({
                                    renderText({ options, node }) {
                                        return `${node.attrs.label ?? node.attrs.id}`
                                    },
                                    HTMLAttributes: {
                                        class: 'mention',
                                    },
                                    deleteTriggerWithBackspace: true,
                                }),
                            ]}
                            content={message}
                        />
                    </Box>
                    {!isLastItem && <Divider />}
                </Stack>
            )
        }
    )

    if (loading) return <LoadingPanel />
    return (
        <Stack gap={4} sx={{ overflow: 'auto', maxHeight: '75vh' }}>
            {cards?.length ? (
                cards
            ) : (
                <Stack alignItems="center" padding={4}>
                    <Typography variant="body1">
                        No blasts have been sent to this roster.
                    </Typography>
                </Stack>
            )}
        </Stack>
    )
}

export default HistoricalBlasts
